/**
 * User-scope chart theme (and one day other per-user prefs).
 *
 * Sits at the bottom of the scope cascade — anything not
 * overridden at the chart-tab or workspace level falls through
 * to this. Persisted server-side under user_settings.json so
 * the user's defaults follow them across workspaces / devices
 * (anywhere the same CF Zero Trust identity authenticates).
 *
 * Loaded once on app boot via `fetchUserTheme`; the dialog
 * commits user-scope edits via `saveUserTheme` (POST). Workspace
 * + chart scopes round-trip through the workspace JSON instead
 * (the existing autosave hook handles those).
 */
import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { api } from "@/api/client";
import type {
  UserIndicatorsPayload,
  UserThemePayload,
  UserWeeklyBehaviourPayload,
} from "@/api/types";
import {
  CHART_SCHEMES,
  DEFAULT_INDICATORS,
  DEFAULT_WEEKLY_BEHAVIOUR,
  type ChartScheme,
  type ChartTheme,
  type IndicatorSettings,
  type WeeklyBehaviourSettings,
} from "./uiSlice";
import type { AppTheme } from "./appearanceSlice";

interface UserPrefsState {
  /** User-default theme (DARK app-mode slot — also what every legacy
   *  save carries). `null` means "no user-scope override — fall
   *  through to built-in scheme defaults". */
  theme: ChartTheme | null;
  /** Per-scheme last-saved customisations (same mechanic as the
   *  workspace-scope themeCache so flipping Red/Green ↔ Mono
   *  within User scope preserves tweaks). DARK slot. */
  themeCache: Partial<Record<ChartScheme, ChartTheme>>;
  /** LIGHT app-mode counterparts. Absent on old saves → light mode
   *  inherits the built-in preset (whose default bg auto-whitens). */
  themeLight: ChartTheme | null;
  themeCacheLight: Partial<Record<ChartScheme, ChartTheme>>;
  /** User-default Weekly Behaviour settings. `null` = inherit
   *  from DEFAULT_WEEKLY_BEHAVIOUR. */
  weeklyBehaviour: WeeklyBehaviourSettings | null;
  /** User-default indicator settings (MA lines + RSI). `null` = inherit
   *  from DEFAULT_INDICATORS. Cascades into every chart tab that has
   *  no workspace-scope override for indicators. */
  indicators: IndicatorSettings | null;
  status: "idle" | "loading" | "succeeded" | "failed";
  saving: boolean;
  error: string | null;
}

const initialState: UserPrefsState = {
  theme: null,
  themeCache: {},
  themeLight: null,
  themeCacheLight: {},
  weeklyBehaviour: null,
  indicators: null,
  status: "idle",
  saving: false,
  error: null,
};

/** Normalise the server payload's free-form dicts into typed
 *  ChartTheme objects. Missing fields get filled in from the
 *  scheme defaults so an older partial save loads cleanly. */
function coerceTheme(
  raw: Record<string, unknown> | null | undefined,
): ChartTheme | null {
  if (!raw) return null;
  const scheme = (raw.scheme as ChartScheme) ?? "redGreen";
  return { ...CHART_SCHEMES[scheme], ...(raw as Partial<ChartTheme>) };
}

function coerceCache(
  raw: Record<string, Record<string, unknown>> | undefined,
): Partial<Record<ChartScheme, ChartTheme>> {
  const out: Partial<Record<ChartScheme, ChartTheme>> = {};
  if (!raw) return out;
  for (const scheme of Object.keys(CHART_SCHEMES) as ChartScheme[]) {
    const cached = raw[scheme];
    if (cached) {
      out[scheme] = {
        ...CHART_SCHEMES[scheme],
        ...(cached as Partial<ChartTheme>),
      };
    }
  }
  return out;
}

export const fetchUserTheme = createAsyncThunk<UserThemePayload>(
  "userPrefs/fetchTheme",
  async () => api.getUserTheme(),
);

export const saveUserTheme = createAsyncThunk<
  UserThemePayload,
  {
    theme: ChartTheme | null;
    themeCache: Partial<Record<ChartScheme, ChartTheme>>;
    themeLight: ChartTheme | null;
    themeCacheLight: Partial<Record<ChartScheme, ChartTheme>>;
  }
>("userPrefs/saveTheme", async ({ theme, themeCache, themeLight, themeCacheLight }) => {
  // Cast through unknown to satisfy the dict-typed wire format.
  const payload: UserThemePayload = {
    theme: theme as unknown as Record<string, unknown> | null,
    themeCache: themeCache as unknown as Record<
      string,
      Record<string, unknown>
    >,
    themeLight: themeLight as unknown as Record<string, unknown> | null,
    themeCacheLight: themeCacheLight as unknown as Record<
      string,
      Record<string, unknown>
    >,
  };
  return api.saveUserTheme(payload);
});

// Hydrate the WB settings from a free-form server dict. Missing
// fields fall back to DEFAULT_WEEKLY_BEHAVIOUR so older saves
// load cleanly after schema additions.
function coerceWeeklyBehaviour(
  raw: Record<string, unknown> | null | undefined,
): WeeklyBehaviourSettings | null {
  if (!raw) return null;
  return {
    ...DEFAULT_WEEKLY_BEHAVIOUR,
    ...(raw as Partial<WeeklyBehaviourSettings>),
  };
}

export const fetchUserWeeklyBehaviour =
  createAsyncThunk<UserWeeklyBehaviourPayload>(
    "userPrefs/fetchWeeklyBehaviour",
    async () => api.getUserWeeklyBehaviour(),
  );

export const saveUserWeeklyBehaviour = createAsyncThunk<
  UserWeeklyBehaviourPayload,
  { settings: WeeklyBehaviourSettings | null }
>("userPrefs/saveWeeklyBehaviour", async ({ settings }) => {
  const payload: UserWeeklyBehaviourPayload = {
    settings: settings as unknown as Record<string, unknown> | null,
  };
  return api.saveUserWeeklyBehaviour(payload);
});

function coerceIndicators(
  raw: Record<string, unknown> | null | undefined,
): IndicatorSettings | null {
  if (!raw) return null;
  return { ...DEFAULT_INDICATORS, ...(raw as Partial<IndicatorSettings>) };
}

export const fetchUserIndicators =
  createAsyncThunk<UserIndicatorsPayload>(
    "userPrefs/fetchIndicators",
    async () => api.getUserIndicators(),
  );

export const saveUserIndicators = createAsyncThunk<
  UserIndicatorsPayload,
  { settings: IndicatorSettings | null }
>("userPrefs/saveIndicators", async ({ settings }) => {
  const payload: UserIndicatorsPayload = {
    settings: settings as unknown as Record<string, unknown> | null,
  };
  return api.saveUserIndicators(payload);
});

const userPrefsSlice = createSlice({
  name: "userPrefs",
  initialState,
  reducers: {
    /** Local-only update (used by the dialog's live preview).
     *  saveUserTheme persists to the server when the user explicitly
     *  Saves at User scope. `mode` selects the dark or light slot. */
    setUserTheme(
      state,
      action: PayloadAction<{ mode: AppTheme; theme: ChartTheme | null }>,
    ) {
      if (action.payload.mode === "light") state.themeLight = action.payload.theme;
      else state.theme = action.payload.theme;
    },
    setUserThemeCache(
      state,
      action: PayloadAction<{
        mode: AppTheme;
        themeCache: Partial<Record<ChartScheme, ChartTheme>>;
      }>,
    ) {
      if (action.payload.mode === "light")
        state.themeCacheLight = action.payload.themeCache;
      else state.themeCache = action.payload.themeCache;
    },
    setUserWeeklyBehaviour(
      state,
      action: PayloadAction<WeeklyBehaviourSettings | null>,
    ) {
      state.weeklyBehaviour = action.payload;
    },
    setUserIndicators(
      state,
      action: PayloadAction<IndicatorSettings | null>,
    ) {
      state.indicators = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserTheme.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchUserTheme.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.theme = coerceTheme(action.payload.theme);
        state.themeCache = coerceCache(action.payload.themeCache);
        state.themeLight = coerceTheme(action.payload.themeLight);
        state.themeCacheLight = coerceCache(action.payload.themeCacheLight);
      })
      .addCase(fetchUserTheme.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message ?? "Failed to load user theme";
      })
      .addCase(saveUserTheme.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveUserTheme.fulfilled, (state, action) => {
        state.saving = false;
        state.theme = coerceTheme(action.payload.theme);
        state.themeCache = coerceCache(action.payload.themeCache);
        state.themeLight = coerceTheme(action.payload.themeLight);
        state.themeCacheLight = coerceCache(action.payload.themeCacheLight);
      })
      .addCase(saveUserTheme.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message ?? "Failed to save user theme";
      })

      .addCase(fetchUserWeeklyBehaviour.fulfilled, (state, action) => {
        state.weeklyBehaviour = coerceWeeklyBehaviour(action.payload.settings);
      })
      .addCase(fetchUserWeeklyBehaviour.rejected, (state, action) => {
        state.error =
          action.error.message ?? "Failed to load user weekly behaviour";
      })
      .addCase(saveUserWeeklyBehaviour.pending, (state) => {
        state.saving = true;
      })
      .addCase(saveUserWeeklyBehaviour.fulfilled, (state, action) => {
        state.saving = false;
        state.weeklyBehaviour = coerceWeeklyBehaviour(action.payload.settings);
      })
      .addCase(saveUserWeeklyBehaviour.rejected, (state, action) => {
        state.saving = false;
        state.error =
          action.error.message ?? "Failed to save user weekly behaviour";
      })

      .addCase(fetchUserIndicators.fulfilled, (state, action) => {
        state.indicators = coerceIndicators(action.payload.settings);
      })
      .addCase(fetchUserIndicators.rejected, (_state, _action) => {
        // Keep null — DEFAULT_INDICATORS cascades in the selector.
      })
      .addCase(saveUserIndicators.pending, (state) => {
        state.saving = true;
      })
      .addCase(saveUserIndicators.fulfilled, (state, action) => {
        state.saving = false;
        state.indicators = coerceIndicators(action.payload.settings);
      })
      .addCase(saveUserIndicators.rejected, (state, action) => {
        state.saving = false;
        state.error =
          action.error.message ?? "Failed to save user indicators";
      });
  },
});

export const {
  setUserTheme,
  setUserThemeCache,
  setUserWeeklyBehaviour,
  setUserIndicators,
} = userPrefsSlice.actions;
export default userPrefsSlice.reducer;
